/**
 * Page header: one compact status dot per lender, and a Lenders panel with each lender's full
 * status (UWM connection, the sheet in use, what the hourly check last saw). Admins switch a
 * lender on or off for the whole company there — a lender that is off is left out of everyone's
 * quotes, emails and scheduled emails until it is turned back on.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { MANA_PRICING_SOURCES, MANA_SOURCE_LABELS, type ManaPricingSource } from '@mana/shared';
import { api } from '../../lib/api.js';
import { COMPANY_LENDERS_KEY, errText, lenderSheetLine, type CompanyLendersDto, type LenderSheetHeaderStatus } from './shared.js';

type Tone = 'ok' | 'warn' | 'err' | 'muted';

interface LenderItem {
  key: ManaPricingSource;
  name: string;
  included: boolean;
  tone: Tone;
  detail: string;
  title?: string;
  /** Priced live on the lender's own engine (not from its rate sheet). */
  live?: boolean;
}

const DOT: Record<Tone, string> = {
  ok: 'bg-emerald-500',
  warn: 'bg-amber-500',
  err: 'bg-rose-500',
  muted: 'bg-slate-300',
};
const DETAIL: Record<Tone, string> = {
  ok: 'text-slate-500',
  warn: 'text-amber-700',
  err: 'text-rose-600',
  muted: 'text-slate-400',
};

const capitalize = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

export function LendersMenu({
  company,
  sheets,
  uwm,
}: {
  company: CompanyLendersDto | undefined;
  sheets: LenderSheetHeaderStatus[];
  uwm: { loading: boolean; error: string | null };
}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const toggle = useMutation({
    mutationFn: (v: { key: ManaPricingSource; included: boolean }) =>
      api<CompanyLendersDto>(`/api/mana-pricing/company-lenders/${v.key}`, { method: 'PUT', body: JSON.stringify({ included: v.included }) }),
    onSuccess: (r) => {
      setMsg(null);
      qc.setQueryData(COMPANY_LENDERS_KEY, r);
      void qc.invalidateQueries({ queryKey: ['mana-sheets'] });
      void qc.invalidateQueries({ queryKey: ['ls-sheets'] });
    },
    onError: (e) => setMsg(errText(e, 'Could not change the lender')),
  });

  const canEdit = company?.canEdit ?? false;
  const items: LenderItem[] = MANA_PRICING_SOURCES.map((key) => {
    const name = MANA_SOURCE_LABELS[key];
    const included = company?.lenders.find((l) => l.key === key)?.included ?? true;
    if (key === 'uwm') {
      const tone: Tone = uwm.loading ? 'muted' : uwm.error ? 'err' : 'ok';
      return { key, name, included, tone, detail: uwm.loading ? 'Connecting…' : uwm.error ? `Not available — ${uwm.error}` : 'Live quotes · connected' };
    }
    const sheet = sheets.find((s) => s.key === key);
    if (!sheet) return { key, name, included, tone: 'muted', detail: 'Checking the rate sheet…' };
    // The sheet's own status, whether or not the lender is switched on.
    const line = lenderSheetLine({ ...sheet, pricingOff: false });
    return { key, name, included, tone: line.tone, detail: capitalize(line.text), title: line.title, live: !!sheet.liveEngine };
  });
  const includedCount = items.filter((i) => i.included).length;
  const offNames = items.filter((i) => !i.included).map((i) => i.name);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={() => setOpen((v) => !v)}
        title={offNames.length ? `Turned off for the company: ${offNames.join(', ')}` : 'Lender status'}
        className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1 rounded-full border border-slate-200 bg-white px-3.5 py-1.5 text-xs shadow-sm transition-colors hover:border-brand-navy-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-navy-300"
      >
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">Lenders</span>
        {items.map((i) => (
          <span key={i.key} className={clsx('flex items-center gap-1.5 whitespace-nowrap', i.included ? 'text-slate-700' : 'text-slate-400 line-through decoration-slate-300')}>
            <span className={clsx('inline-block h-2 w-2 rounded-full', i.included ? DOT[i.tone] : 'border border-slate-300 bg-transparent')} />
            {i.name}
          </span>
        ))}
        <span className="text-slate-400" aria-hidden>
          {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div role="dialog" aria-label="Lenders" className="absolute right-0 top-full z-40 mt-2 w-[min(30rem,calc(100vw-2rem))] rounded-xl border border-slate-200 bg-white p-4 text-left shadow-floating">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-brand-navy-800">Lenders</p>
              <p className="mt-0.5 text-xs text-slate-500">
                {canEdit
                  ? 'Checked lenders are priced for everyone in the company — quotes, emails and scheduled emails.'
                  : 'Your admin chooses which lenders the company prices.'}
              </p>
            </div>
            <button type="button" aria-label="Close" onClick={() => setOpen(false)} className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
              ✕
            </button>
          </div>

          <ul className="mt-3 divide-y divide-slate-100 border-y border-slate-100">
            {items.map((i) => {
              const lastOn = i.included && includedCount === 1;
              const body = (
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-x-2 text-sm font-medium text-slate-800">
                    {i.name}
                    <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{i.key === 'uwm' || i.live ? 'live' : 'rate sheet'}</span>
                    {!i.included && <span className="rounded bg-slate-100 px-1.5 py-px text-[10px] font-semibold uppercase tracking-wide text-slate-500">Off for the company</span>}
                  </p>
                  <p className={clsx('mt-0.5 text-xs', i.included ? DETAIL[i.tone] : 'text-slate-400')} title={i.title}>
                    {i.detail}
                  </p>
                </div>
              );
              return (
                <li key={i.key}>
                  {canEdit ? (
                    <label className={clsx('flex items-start gap-3 py-2.5', lastOn || toggle.isPending ? 'cursor-not-allowed' : 'cursor-pointer')} title={lastOn ? 'At least one lender has to stay on' : undefined}>
                      <input
                        type="checkbox"
                        checked={i.included}
                        disabled={toggle.isPending || lastOn}
                        onChange={(e) => toggle.mutate({ key: i.key, included: e.target.checked })}
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 accent-brand-navy-600"
                        aria-label={`Include ${i.name} in pricing for the company`}
                      />
                      {body}
                    </label>
                  ) : (
                    <div className="flex items-start gap-3 py-2.5">
                      <span className={clsx('mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full', i.included ? DOT[i.tone] : 'border border-slate-300')} />
                      {body}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {msg && <p className="mt-2 text-xs text-rose-600">{msg}</p>}
          {canEdit && <p className="mt-2 text-[11px] text-slate-400">Turning a lender back on prices it right away — live lenders are asked on every quote, and rate sheets keep updating while a lender is off.</p>}
        </div>
      )}
    </div>
  );
}
